import React, { memo, useState } from 'react';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import WorkflowStopRunDialog from './WorkflowStopRunDialog';
import type { WorkflowRun, WorkflowNode } from '../../../types/backend';

interface Props {
  run: WorkflowRun;
  nodes: WorkflowNode[];
  onResolveGate: (nodeId: string, approved: boolean, note?: string) => Promise<void>;
  onTakeBranch: (nodeId: string, condition: string) => Promise<void>;
}

// The one thing this run is waiting on the operator for, as a band under the drawing with the
// answer on it. A run parks for exactly two reasons, and both are answered here rather than in a
// menu somewhere else: a gate wants yes or no, and a decision wants one of its own routes.
//
// The routes are the buttons. A decision's branches are already the complete set of answers, so
// offering anything else would invent an outcome the workflow cannot take.
const WorkflowPrompt = memo<Props>(({ run, nodes, onResolveGate, onTakeBranch }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The gate the stop dialog belongs to, not a flag that one is open. A gate resolved elsewhere
  // while the dialog is up must close it, never leave the next gate pre-confirmed, and the note
  // is cleared on open so text typed against one gate cannot be recorded against another.
  const [stoppingNode, setStoppingNode] = useState<string | null>(null);
  const [note, setNote] = useState('');

  const openGate = (run.gates ?? []).find((g) => !g.resolution);
  const decided = new Set((run.decisions ?? []).map((d) => d.node_id));
  const openDecision = nodes.find(
    (n) =>
      n.kind === 'decision'
      && n.layer === run.current_layer
      && !decided.has(n.id)
      && run.node_states?.[n.id] !== 'pruned',
  );

  const act = async (write: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await write();
    } catch (e) {
      // A refusal that vanished would leave the operator believing the run moved on.
      setError(e instanceof Error ? e.message : 'That did not reach the run');
    } finally {
      setBusy(false);
    }
  };

  if (!openGate && !openDecision) return error ? <Alert severity="error">{error}</Alert> : null;

  const gateNode = openGate ? nodes.find((n) => n.id === openGate.node_id) : undefined;

  return (
    <Box
      sx={(theme) => ({
        p: 1.5,
        bgcolor: alpha(theme.palette.primary.main, 0.08),
        borderTop: 1,
        borderColor: 'divider',
      })}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {openGate && (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="body2" sx={{ flex: 1, minWidth: 180 }}>
            <strong>{gateNode?.label ?? openGate.node_id}</strong> needs your approval
            {openGate.reason ? `: ${openGate.reason}` : ''}
          </Typography>
          <Button
            size="small"
            variant="contained"
            disabled={busy}
            onClick={() => act(() => onResolveGate(openGate.node_id, true))}
          >
            Approve
          </Button>
          {/* Declining a gate abandons the whole run: WorkflowRun.resolve_gate sets status to
              ABANDONED on a REJECTED resolution. The label carries that blast radius, and the
              confirmation exists because nothing else on this band pauses an irreversible act. */}
          <Button
            size="small"
            variant="outlined"
            disabled={busy}
            onClick={() => {
              setStoppingNode(openGate.node_id);
              setNote('');
            }}
          >
            Stop run
          </Button>
        </Stack>
      )}

      {openGate && (
        <WorkflowStopRunDialog
          open={stoppingNode === openGate.node_id}
          stepLabel={gateNode?.label ?? openGate.node_id}
          note={note}
          onNoteChange={setNote}
          onCancel={() => {
            setStoppingNode(null);
            setNote('');
          }}
          onConfirm={() => {
            if (busy) return;
            const given = note.trim();
            setStoppingNode(null);
            setNote('');
            void act(() => onResolveGate(openGate.node_id, false, given || undefined));
          }}
        />
      )}

      {!openGate && openDecision && (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="body2" sx={{ flex: 1, minWidth: 180 }}>
            {openDecision.label}
          </Typography>
          {(openDecision.branches ?? []).map((branch, i) => (
            <Button
              key={branch.condition}
              size="small"
              variant={i === 0 ? 'contained' : 'outlined'}
              disabled={busy}
              onClick={() => act(() => onTakeBranch(openDecision.id, branch.condition))}
            >
              {branch.condition}
            </Button>
          ))}
        </Stack>
      )}
    </Box>
  );
});

WorkflowPrompt.displayName = 'WorkflowPrompt';

export default WorkflowPrompt;
